import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2, H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

import type { ContactLocation, FlowState } from '../types/booking.type'

type ErrorContentProps = {
  flowState?: FlowState
  isLoading?: boolean
  onGoBack?: () => void
}

export const ErrorContent = ({ flowState, isLoading, onGoBack }: ErrorContentProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const handleClose = () => {
    mixpanel.track('booking_flow_closed', {
      step: 'error',
      withTriage: !!flowState?.triagePath?.length,
    })
    navigate('/home', { replace: true })
  }

  const handlePhoneClick = (phoneNumber: string, locationName: string) => {
    mixpanel.track('booking_flow_error_phone_clicked', {
      phoneNumber,
      locationName,
    })
    window.open(`tel:${phoneNumber}`, '_self')
  }

  const renderContactLocation = (location: ContactLocation) => (
    <Card key={location.id}>
      <FlexColumn gap={theme.UI.SpacingPx.Space3}>
        <FlexColumn gap={theme.UI.SpacingPx.Space1}>
          <H3 noMargin>{location.name}</H3>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {location.profession}
          </Span>
        </FlexColumn>
        <Line />
        <FlexRow justifyContent='space-between' alignItems='center'>
          <Span type='BodyMediumRegular'>{location.phoneNumberDisplay || location.phoneNumber}</Span>
          <IconButton
            variant='Secondary'
            icon={['fal', 'phone']}
            size={'small'}
            onClick={() => handlePhoneClick(location.phoneNumber, location.name)}
          />
        </FlexRow>
      </FlexColumn>
    </Card>
  )

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <H2 noMargin>
          {t({
            id: 'appointments.book.error.title',
            comment: 'Error title',
            message: 'Er liep iets mis',
          })}
        </H2>
        <Span type='BodyMediumRegular'>
          {t({
            id: 'appointments.book.error.description',
            comment: 'Error description',
            message: 'Neem telefonisch contact op met het ziekenhuis om je afspraak te boeken.',
          })}
        </Span>

        {flowState?.contactLocations && flowState.contactLocations.length > 0 ? (
          flowState.contactLocations.sort((a, b) => a.id - b.id).map(renderContactLocation)
        ) : (
          <Card>
            <FlexColumn gap={theme.UI.SpacingPx.Space3}>
              <FlexColumn gap={theme.UI.SpacingPx.Space1}>
                <H3 noMargin>
                  {flowState?.specialisation?.title?.nl ||
                    t({
                      id: 'appointments.book.contact.noData.title',
                      comment: 'No contact data title',
                      message: 'Geen contactgegevens beschikbaar',
                    })}
                </H3>
                {!flowState?.specialisation?.title?.nl && (
                  <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                    Onbekende specialisatie
                  </Span>
                )}
              </FlexColumn>
              <Line />

              <Span type='BodyMediumRegular'>
                {t({
                  id: 'appointments.book.contact.noData.description',
                  comment: 'No contact data description',
                  message: 'Neem contact op via de algemene lijn van het ziekenhuis.',
                })}
              </Span>
            </FlexColumn>
          </Card>
        )}
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <FlexColumn gap={theme.UI.SpacingPx.Space2}>
          {onGoBack && (
            <Button variant='Secondary' onClick={onGoBack} disabled={isLoading}>
              {t({ id: 'globals.previous-step', message: 'Vorige stap' })}
            </Button>
          )}
          <Button variant='Primary' onClick={handleClose} disabled={isLoading} isLoading={isLoading}>
            {t({ id: 'globals.close', message: 'Sluiten' })}
          </Button>
        </FlexColumn>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}
